import { parseColor } from "@/document/model/color";
import {
  createDefaultStyle,
  createSolidPaint,
  type NodeStyle,
  type Paint,
  type StrokeStyle,
} from "@/document/model/styles";
import type { Rect } from "@/lib/math/rect";
import type { SvgDefsRegistry } from "./svgDefs";
import { parseUrlReference } from "./svgDefs";
import { resolveElementEffects } from "./svgEffects";
import type { SvgImportIssueCollector } from "./issues";
import { readSvgLength } from "./parseSvgDocument";

export interface SvgPresentationContext {
  fill?: Paint | null;
  stroke?: StrokeStyle | null;
  opacity: number;
  fillRule: "nonzero" | "evenodd";
  display: boolean;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number;
}

export function createDefaultSvgPresentationContext(): SvgPresentationContext {
  return {
    fill: createSolidPaint("#000000"),
    stroke: null,
    opacity: 1,
    fillRule: "nonzero",
    display: true,
  };
}

export function mergeSvgPresentationContext(
  parent: SvgPresentationContext,
  element: Element,
  issues: SvgImportIssueCollector,
): SvgPresentationContext {
  const inline = parseInlineStyle(element.getAttribute("style"));
  const fill = readPaintAttribute(element, "fill", inline, parent.fill, issues, undefined, undefined);
  const strokePaint = readPaintAttribute(element, "stroke", inline, parent.stroke?.paint, issues, undefined, undefined);
  const strokeWidth = readSvgLength(inline.stroke ?? element.getAttribute("stroke-width") ?? undefined, parent.stroke?.width ?? 1);
  const opacity = readSvgLength(inline.opacity ?? element.getAttribute("opacity") ?? undefined, parent.opacity);
  const fillOpacity = readSvgLength(inline["fill-opacity"] ?? element.getAttribute("fill-opacity") ?? undefined, 1);
  const strokeOpacity = readSvgLength(inline["stroke-opacity"] ?? element.getAttribute("stroke-opacity") ?? undefined, 1);
  const displayValue = inline.display ?? element.getAttribute("display");
  const fillRule = (inline["fill-rule"] ?? element.getAttribute("fill-rule") ?? parent.fillRule) as "nonzero" | "evenodd";

  let stroke: StrokeStyle | null = parent.stroke ?? null;
  if (strokePaint === null) {
    stroke = null;
  } else if (strokePaint) {
    stroke = {
      paint: applyOpacity(strokePaint, strokeOpacity),
      width: strokeWidth,
      align: "center",
    };
  }

  return {
    fill: fill === null ? null : fill ? applyOpacity(fill, fillOpacity) : parent.fill,
    stroke,
    opacity,
    fillRule: fillRule === "evenodd" ? "evenodd" : "nonzero",
    display: displayValue ? displayValue !== "none" : parent.display,
    fontFamily: inline["font-family"] ?? element.getAttribute("font-family") ?? parent.fontFamily,
    fontSize: readSvgLength(inline["font-size"] ?? element.getAttribute("font-size") ?? undefined, parent.fontSize ?? 16),
    fontWeight: readFontWeight(inline["font-weight"] ?? element.getAttribute("font-weight") ?? undefined, parent.fontWeight ?? 400),
  };
}

export function nodeStyleFromSvgPresentation(presentation: SvgPresentationContext): NodeStyle {
  const base = createDefaultStyle("#000000");
  const fills = presentation.fill ? [presentation.fill] : [];
  const strokes = presentation.stroke ? [presentation.stroke] : [];
  return {
    ...base,
    fills,
    strokes,
    stroke: strokes[0],
    opacity: presentation.opacity,
  };
}

export function nodeStyleFromSvgElement(
  element: Element,
  presentation: SvgPresentationContext,
  bounds: Rect,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
): NodeStyle {
  const inline = parseInlineStyle(element.getAttribute("style"));
  const fill = readPaintAttribute(element, "fill", inline, presentation.fill, issues, defs, bounds);
  const strokePaint = readPaintAttribute(element, "stroke", inline, presentation.stroke?.paint, issues, defs, bounds);
  const strokeWidth = readSvgLength(inline["stroke-width"] ?? element.getAttribute("stroke-width") ?? undefined, presentation.stroke?.width ?? 1);
  const strokeOpacity = readSvgLength(inline["stroke-opacity"] ?? element.getAttribute("stroke-opacity") ?? undefined, 1);
  const fillOpacity = readSvgLength(inline["fill-opacity"] ?? element.getAttribute("fill-opacity") ?? undefined, 1);

  const fills = fill === null ? [] : fill ? [applyOpacity(fill, fillOpacity)] : presentation.fill ? [presentation.fill] : [];
  const strokes: StrokeStyle[] = [];
  if (strokePaint) {
    strokes.push({
      paint: applyOpacity(strokePaint, strokeOpacity),
      width: strokeWidth,
      align: "center",
    });
  } else if (strokePaint !== null && presentation.stroke) {
    strokes.push(presentation.stroke);
  }

  const effects = resolveElementEffects(element, defs, issues);

  return {
    ...createDefaultStyle("#000000"),
    fills,
    strokes,
    stroke: strokes[0],
    effects: effects.length > 0 ? effects : undefined,
    opacity: readSvgLength(inline.opacity ?? element.getAttribute("opacity") ?? undefined, presentation.opacity),
  };
}

export function parseFontFamilyList(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }
  return value
    .split(",")
    .map(part => part.trim().replace(/^['"]|['"]$/g, ""))
    .filter(Boolean);
}

export function resolveSvgFontFamily(
  value: string | null | undefined,
  catalogFamilies: readonly string[],
  issues: SvgImportIssueCollector,
  elementName?: string,
): string {
  const requested = parseFontFamilyList(value)[0] ?? "Inter";
  const match = catalogFamilies.find(family => family.toLowerCase() === requested.toLowerCase());
  if (match) {
    return match;
  }
  issues.add({
    code: "fallback-font",
    message: `Font "${requested}" is not in the Google Fonts catalog; using Inter`,
    element: elementName,
  });
  return "Inter";
}

function readPaintAttribute(
  element: Element,
  attribute: "fill" | "stroke",
  inline: Record<string, string>,
  inherited: Paint | null | undefined,
  issues: SvgImportIssueCollector,
  defs: SvgDefsRegistry | undefined,
  bounds: Rect | undefined,
): Paint | null | undefined {
  const raw = inline[attribute] ?? element.getAttribute(attribute);
  if (raw === undefined) {
    return inherited;
  }
  if (!raw || raw === "none" || raw === "transparent") {
    return null;
  }
  if (raw.startsWith("url(")) {
    if (defs && bounds) {
      const paint = defs.resolvePaint(raw, bounds, issues);
      if (paint) {
        return paint;
      }
    }
    const id = parseUrlReference(raw);
    issues.addOnce(
      "unsupported-gradient",
      `Could not resolve ${attribute} reference "${raw}"${id ? ` (#${id})` : ""}`,
      element.localName ?? element.nodeName,
    );
    return inherited ?? createSolidPaint("#000000");
  }
  try {
    return createSolidPaint(parseColor(raw));
  } catch {
    issues.add({
      code: "unsupported-attribute",
      message: `Could not parse ${attribute} value "${raw}"`,
      element: element.localName ?? element.nodeName,
    });
    return inherited ?? createSolidPaint("#000000");
  }
}

function parseInlineStyle(style: string | null): Record<string, string> {
  if (!style) {
    return {};
  }
  const entries: Record<string, string> = {};
  for (const declaration of style.split(";")) {
    const separator = declaration.indexOf(":");
    if (separator <= 0) {
      continue;
    }
    const key = declaration.slice(0, separator).trim().toLowerCase();
    const value = declaration.slice(separator + 1).trim();
    if (key) {
      entries[key] = value;
    }
  }
  return entries;
}

function applyOpacity(paint: Paint, opacity: number): Paint {
  if (opacity === 1) {
    return paint;
  }
  if (paint.type === "solid") {
    return {
      ...paint,
      color: {
        ...paint.color,
        alpha: paint.color.alpha * opacity,
      },
    };
  }
  if (paint.type === "linearGradient" || paint.type === "radialGradient") {
    return {
      ...paint,
      stops: paint.stops.map(stop => ({
        ...stop,
        color: {
          ...stop.color,
          alpha: stop.color.alpha * opacity,
        },
      })),
    };
  }
  if (paint.type === "image") {
    return {
      ...paint,
      opacity: paint.opacity * opacity,
    };
  }
  return paint;
}

function readFontWeight(value: string | undefined, fallback: number): number {
  if (!value) {
    return fallback;
  }
  if (value === "bold") {
    return 700;
  }
  if (value === "normal") {
    return 400;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}
